import { ReactElement, ReactNode, useState } from 'react';

import styled from 'styled-components';

import { Collapse, CollapseProps } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Badge } from '@components/badge';
import { IconProps } from '@components/icons';
import { IconDropRight } from '@components/icons/dropright';
import { IconErrorSmall } from '@components/icons/error-small';
import { IconInfoSmall } from '@components/icons/info-small';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { Stack } from '../stack';

export const HIDE_CONTAINER_HEADER_CLASSNAME = 'hide-container-header';

export interface HideContainerProps {
  title: string;
  children: ReactNode;
  icon?: ReactElement<IconProps>;
  info?: string;
  defaultClosed?: boolean;
  error?: boolean;
  counter?: ReactNode;
  fraction?: boolean;
  caps?: boolean;
  overflowType?: CollapseProps['overflowType'];
  onClick?: () => void;
}

export function HideContainer(props: HideContainerProps) {
  const {
    title,
    children,
    icon,
    info,
    defaultClosed = false,
    error = false,
    counter,
    fraction = false,
    caps = false,
    overflowType = 'auto',
    onClick,
  } = props;
  const [isOpen, setIsOpen] = useState(!defaultClosed);

  return (
    <div>
      <div
        onClick={() => {
          setIsOpen(!isOpen);
          onClick?.();
        }}
      >
        <StyledHeaderContainer>
          <Stack space={2} orientation="horizontal" align="center">
            {icon && <icon.type {...icon.props} color="elementPurple50" data-hover />}
            <Text font="semibold14" text={title} caps={caps} data-hover-text />
            {counter !== undefined && (
              <Badge size="md" fraction={fraction}>
                {counter}
              </Badge>
            )}
            {info && <IconInfoSmall color="elementPurple50" tooltip={info} />}
          </Stack>
          <Stack orientation="horizontal" space={2} align="center">
            {error && <IconErrorSmall color="error" />}
            <Rotate isRotated={isOpen}>
              <IconDropRight color="buttonIconPositive" data-hover-icon />
            </Rotate>
          </Stack>
        </StyledHeaderContainer>
      </div>
      <Collapse isCollapsed={!isOpen} keepCollapsedInDom overflowType={overflowType}>
        <ContentContainer>{children}</ContentContainer>
      </Collapse>
    </div>
  );
}

const StyledHeaderContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 1,
  className: HIDE_CONTAINER_HEADER_CLASSNAME,
})({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: pxToRem(6),
  cursor: 'pointer',
  padding: `${pxToRem(4)} ${pxToRem(4)} ${pxToRem(4)} 0`,

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `
        linear-gradient(
          90deg,
          transparent 0%,
          ${theme.color.uiTrans10} 100%
        )
      `,
    opacity: 0,
    transition: 'opacity 300ms ease-in-out',
  },

  '&:hover::before': {
    color: theme.color.textSecondary,
    opacity: 1,
  },

  '&:hover [data-hover-text]': {
    color: theme.color.textSecondary,
  },

  '&:hover [data-hover-icon]': {
    color: theme.color.buttonIconPositiveHover,
  },
});

const ContentContainer = styled.div({
  marginTop: pxToRem(16),
});
